import React from 'react';

interface PropertyRowProps {
  label: string;
  children: React.ReactNode;
}

/**
 * Label-left, control-right row — the compact "native app property panel"
 * layout (label + inline value), as opposed to a stacked web-form field
 * (label above a full-width boxed input). Control side is expected to size
 * itself (e.g. via `propertyInputStyle`/`propertySelectStyle` below); this
 * component only owns the row's label column + alignment.
 */
export function PropertyRow({ label, children }: PropertyRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 27 }}>
      <label style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, width: 78 }}>
        {label}
      </label>
      <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
        {children}
      </div>
    </div>
  );
}

// Flattened, low-chrome field style — a subtle filled background instead of a
// boxed border, matching the reference's inline-editable-value look (e.g.
// Figma's own numeric property fields) rather than a bordered web-form input.
// Apply alongside the "panel-field" className so the :focus rule in index.css
// applies (a plain style prop can't express :focus).
export const PANEL_FIELD_CLASS = 'panel-field';

export const propertyInputStyle: React.CSSProperties = {
  width: '100%', padding: '4px 8px', border: '1px solid transparent',
  borderRadius: 6, background: 'var(--panel-field-bg, #f1f2f4)',
  fontSize: 12.5, color: 'var(--text)',
};

export const propertySelectStyle: React.CSSProperties = {
  ...propertyInputStyle,
  cursor: 'pointer',
};
